import axios from 'axios'
import type { ApiResponse } from '../../types'
import type { BootstrapRequest, ConsentRecord, MeResponse } from '../../types/community'
import { client, unwrap, apiError, CotApiError } from './client'

// COTC_SignupConsent owns the bodies. Foundation shipped the bootstrap/consent signatures; this
// module also carries the shared-platform registration integration (R1) so the wizard can drive
// register → login → bootstrap without inventing a new auth authority.

const AUTH_BASE = (import.meta.env.VITE_AUTH_URL as string | undefined) || (import.meta.env.VITE_API_URL as string | undefined) || ''

const authClient = axios.create({
  baseURL: AUTH_BASE,
  headers: { 'Content-Type': 'application/json' },
})

// ── Shared-platform acknowledgements + registration (R1) ─────────────────────

/**
 * Version id of the 13+ age-attestation copy this client collected, mirroring
 * `AgeEligibilityCopy.CurrentVersion` on the server and the constant the Knowz web client sends
 * (`AGE_ELIGIBILITY_COPY_VERSION` in components/auth/AgeEligibilityAttestation.tsx).
 *
 * This is NOT in the `/register/acknowledgements` catalogue — that returns only terms/privacy/
 * data-care. Age eligibility travels in its own `ageEligibility*` request fields, and the server
 * compares this exact string: a missing or mismatched value is rejected as StaleCopy with
 * "The age confirmation text has been updated. Please review it and confirm again." Sending the
 * other three acknowledgements without it produced exactly that, so registration could never
 * complete. Bump this when the platform bumps its copy version.
 * WorkGroupID: kc-feat-circle-of-trust-site-20260821-013216 (deployment phase)
 */
export const AGE_ELIGIBILITY_COPY_VERSION = 'age_eligibility.v1'
export interface AckRequirement {
  kind: string
  copyVersion: string
  url?: string | null
  title?: string | null
  text?: string | null
}

export interface AckCatalog {
  acknowledgements: AckRequirement[]
  defaultSurface: string
  legalReviewRequired: boolean
}

/** GET /api/v1/users/register/acknowledgements — the current platform ack requirements. */
export async function getAcknowledgements(): Promise<AckCatalog> {
  try {
    const res = await authClient.get<ApiResponse<AckCatalog>>('/api/v1/users/register/acknowledgements')
    return unwrap(res.data)
  } catch (err) {
    throw apiError(err)
  }
}

export interface RegisterParams {
  legalName: string
  email: string
  password: string
  dateOfBirth: string
  /** The current-version acks the user affirmatively accepted at step 2. */
  acknowledgements: AckRequirement[]
  ageEligibilitySurface?: string
}

/** POST /api/v1/users/register — creates the shared-platform identity for a CoT account (R1). */
export async function registerCircleOfTrust(params: RegisterParams): Promise<void> {
  const legal = params.legalName.trim()
  const tokens = legal.split(/\s+/).filter(Boolean)
  const firstName = tokens[0] ?? legal
  const lastName = tokens.length > 1 ? tokens.slice(1).join(' ') : firstName

  const body = {
    username: params.email.trim(),
    email: params.email.trim(),
    password: params.password,
    firstName,
    lastName,
    displayName: legal,
    registrationSource: 'circleoftrust',
    acknowledgements: params.acknowledgements.map((a) => ({
      kind: a.kind,
      copyVersion: a.copyVersion,
      accepted: true,
    })),
    acknowledgementSurface: params.ageEligibilitySurface ?? 'web_register',
    ageEligibilityAttested: true,
    ageEligibilityCopyVersion: AGE_ELIGIBILITY_COPY_VERSION,
    ageEligibilitySurface: params.ageEligibilitySurface ?? 'web_register',
    ageEligibilityUnder13: false,
  }

  try {
    const res = await authClient.post<ApiResponse<unknown>>('/api/v1/users/register', body)
    unwrap(res.data)
  } catch (err) {
    throw apiError(err)
  }
}

// ── Community bootstrap (phase 2) + consent record ───────────────────────────

/** POST /account/bootstrap — creates CotFamily + persons + consent atomically. */
export async function bootstrapAccount(req: BootstrapRequest): Promise<MeResponse> {
  try {
    const res = await client.post<ApiResponse<MeResponse>>('/account/bootstrap', req)
    return unwrap(res.data)
  } catch (err) {
    if (err instanceof CotApiError) throw err
    throw apiError(err)
  }
}

/** GET /consents/record — the downloadable consent document (JSON or printable text). */
export async function getConsentRecord(format: 'json' | 'text' = 'json'): Promise<ConsentRecord | string> {
  try {
    if (format === 'text') {
      const res = await client.get<string>('/consents/record', {
        params: { format: 'text' },
        responseType: 'text',
      })
      return res.data
    }
    const res = await client.get<ApiResponse<ConsentRecord>>('/consents/record')
    return unwrap(res.data)
  } catch (err) {
    if (err instanceof CotApiError) throw err
    throw apiError(err)
  }
}
